import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { FlaskConical, FileSpreadsheet, ClipboardCheck, MessageSquare, Users, ArrowRight, BarChart3, ShieldCheck, AlertOctagon, FileText } from "lucide-react";
import logger from "@/lib/logger";

export default function Clinical() {
  const [stats, setStats] = useState({ studies: 0, forms: 0, subjects: 0, entries: 0, openQueries: 0, adverseEvents: 0, cers: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [s, f, subj, e, q, ae, cer] = await Promise.all([
          api.get("/studies"), api.get("/ecrf/forms"), api.get("/subjects"),
          api.get("/ecrf/entries"), api.get("/ecrf/queries?state=Open"),
          api.get("/clinical/adverse-events"), api.get("/clinical/cer"),
        ]);
        setStats({
          studies: s.data.length, forms: f.data.length,
          subjects: subj.data.length, entries: e.data.length,
          openQueries: q.data.length,
          adverseEvents: ae.data.length, cers: cer.data.length,
        });
      } catch (err) { logger.warn("Clinical hub stats fetch failed:", err?.response?.status); }
    })();
  }, []);

  const cards = [
    { to: "/clinical/studies", icon: FlaskConical, title: "Studies", desc: "Protocols, endpoints, subject rosters.", count: stats.studies, testId: "clinical-studies" },
    { to: "/clinical/forms", icon: FileSpreadsheet, title: "eCRF Forms", desc: "Visual builder with 7-principle guardrails + AI assist.", count: stats.forms, testId: "clinical-forms" },
    { to: "/clinical/data-entry", icon: ClipboardCheck, title: "Data Entry", desc: "Subject → visit → form. Skip-logic driven.", count: stats.entries, testId: "clinical-data-entry" },
    { to: "/clinical/queries", icon: MessageSquare, title: "Query Workbench", desc: "Discrepancy resolution + SDV sign-off.", count: stats.openQueries, badge: stats.openQueries > 0 ? "open" : null, testId: "clinical-queries" },
    { to: "/clinical/adverse-events", icon: AlertOctagon, title: "Adverse Events / SAE", desc: "ISO 14155 / FDA 21 CFR 803 / EU MDR Art. 87 vigilance timers.", count: stats.adverseEvents, testId: "clinical-adverse-events" },
    { to: "/clinical/cer", icon: FileText, title: "CER Generator", desc: "MDCG 2020-13 aligned Clinical Evaluation Report scaffold.", count: stats.cers, testId: "clinical-cer" },
    { to: "/clinical/consent", icon: ClipboardCheck, title: "eConsent", desc: "Subject-facing tokenised consent capture with e-signature.", count: null, testId: "clinical-consent" },
    { to: "/patient-advisory", icon: Users, title: "Patient Advisory & Co-Design", desc: "Sessions, feedback, co-design outcomes.", count: null, testId: "clinical-patient-advisory" },
  ];

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="vq-overline mb-2">Clinical · Safety by design</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">
          Clinical Studies &amp; eCRF Hub
        </h1>
        <p className="text-slate-600 mt-2 max-w-3xl mb-6">
          Regulation, quality, and patient safety belong at the <b>foundation</b> of every device — not bolted on
          at submission time. This hub is built around the 7 principles of good eCRF design so your first study is
          your best study: cleaner data, faster approvals, safer patients.
        </p>
        <div className="vq-card p-4 mb-8 border-l-4 border-l-emerald-500 bg-emerald-50/40 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 mt-0.5">
            <ShieldCheck className="w-4 h-4" />
          </div>
          <div>
            <div className="font-display font-bold text-sm text-slate-900 mb-1">Idea → market, affordably</div>
            <p className="text-xs text-slate-700 leading-relaxed">
              VitaQMS is designed for founders and small teams who want to build medical devices <b>the right way from
              day one</b>. Every module — from device classification through clinical data capture to post-market
              surveillance — enforces the same principle: safety and compliance are the cheapest when they are the
              earliest.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((c) => (
            <Link key={c.to} to={c.to} data-testid={c.testId}
                  className="vq-card p-6 hover:border-[#0055FF] hover:shadow-md transition-all group">
              <div className="flex items-start justify-between mb-3">
                <c.icon className="w-8 h-8 text-[#0055FF]" />
                {c.count !== null && (
                  <div className="text-right">
                    <div className="font-display font-bold text-2xl text-slate-900 leading-none">{c.count}</div>
                    {c.badge && <div className="text-[9px] font-mono uppercase text-red-600 mt-0.5">{c.badge}</div>}
                  </div>
                )}
              </div>
              <div className="font-display font-bold text-lg text-slate-900 mb-1">{c.title}</div>
              <div className="text-sm text-slate-600 mb-3">{c.desc}</div>
              <div className="text-xs font-mono text-[#0055FF] group-hover:underline inline-flex items-center gap-1">
                Open <ArrowRight className="w-3 h-3" />
              </div>
            </Link>
          ))}
        </div>

        <div className="vq-card p-6 mt-8 border-l-4 border-l-[#0055FF]">
          <div className="flex items-center gap-2 mb-2"><BarChart3 className="w-5 h-5 text-[#0055FF]" />
            <div className="font-display font-bold text-lg text-slate-900">The 7 Principles — Enforced</div></div>
          <ul className="text-sm text-slate-700 space-y-1 mt-3 columns-1 md:columns-2">
            <li>1. Boolean = <b>Yes / No</b> — no &ldquo;check if yes&rdquo; ambiguity.</li>
            <li>2. Mandatory + explicit <b>Missing / N/A</b> with reason.</li>
            <li>3. <b>Choices over free text</b>; long-text requires justification.</li>
            <li>4. <b>Skip logic</b> hides irrelevant fields automatically.</li>
            <li>5. <b>Clone from templates</b> — never rebuild from scratch.</li>
            <li>6. <b>Min/max/unit/decimals</b> guardrails on numbers.</li>
            <li>7. Every field <b>linked to a study endpoint</b> — no data debt.</li>
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
}
